import {
	closestCenter,
	DndContext,
	DragEndEvent,
	MouseSensor,
	TouchSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { arrayMove, horizontalListSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import { styles } from "@repo/helpers";

import { Column } from "../models";
import style from "../style.module.scss";
import { PassThruProps as ColumnPassThruProps, SortableColumn } from "./column";

interface HeaderProps<T> extends PassThruProps {
	columns: Column<T>[];
	columnOrder?: string[];
	setColumnOrder?: (columnOrder: string[]) => void;
}

export interface PassThruProps extends ColumnPassThruProps {
	headerClassName?: string;
}

/**
 * Get the table head for the grid
 */
export function Header<T>(props: HeaderProps<T>) {
	const { columns, headerClassName, columnOrder, setColumnOrder } = props;
	// Touch reorders on press-and-hold so a swipe across the header still scrolls the table.
	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
		useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
	);
	const handleDragEnd = (event: DragEndEvent) => {
		if (!props.reorderable) return;
		const { active, over } = event;
		if (active.id !== over?.id) {
			const oldIndex = columnOrder.indexOf(active.id as string);
			const newIndex = columnOrder.indexOf(over.id as string);
			const newArray = arrayMove(columnOrder, oldIndex, newIndex);
			setColumnOrder(newArray);
		}
	};

	if (!columns?.length) return null;
	return (
		<thead>
			<tr className={styles(style.tableHeader, headerClassName)}>
				<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
					<SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
						{columns.map(renderColumn)}
					</SortableContext>
				</DndContext>
			</tr>
		</thead>
	);

	function renderColumn(column: Column<T>) {
		return <SortableColumn<T> {...props} key={column.id} column={column} />;
	}
}
